import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Trash2, ShoppingBag, ArrowRight, Tag, Truck, Plus } from "lucide-react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { useQuery } from "@tanstack/react-query";
import { useCart } from "@/hooks/useCart";
import { addToCart, cartSubtotalCents, clearCart, removeFromCart, setDiscount, updateQuantity } from "@/lib/cart";
import { formatCents } from "@/lib/format";
import { getStripe, getStripeEnvironment, isPaymentsConfigured } from "@/lib/stripe";
import { createCheckoutSession, getCartUpsell, validateDiscountCode } from "@/lib/payments.functions";
import { getStoredUtm, track } from "@/lib/tracking";

const FREE_SHIP_THRESHOLD = 5000; // mirror server default for client display

export const Route = createFileRoute("/cart")({
  head: () => ({
    meta: [
      { title: "Cart — Glow Long Golf" },
      { name: "description", content: "Review your Glow Long Golf order." },
    ],
  }),
  component: CartPage,
});

function CartPage() {
  const cart = useCart();
  const subtotal = cartSubtotalCents(cart);
  const [checkingOut, setCheckingOut] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [codeInput, setCodeInput] = useState(cart.discountCode ?? "");
  const [codeStatus, setCodeStatus] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    track("view_cart", {
      currency: "USD",
      value: subtotal / 100,
      items: cart.items.map((i) => ({
        item_id: i.variantId,
        item_name: `${i.productTitle} — ${i.variantName}`,
        item_variant: i.variantName,
        price: i.unitPriceCents / 100,
        quantity: i.quantity,
      })),
    });
  }, [cart.items, subtotal]);

  const { data: upsellData } = useQuery({
    queryKey: ["cart-upsell", cart.items.map((i) => i.variantId).join(",")],
    queryFn: () => getCartUpsell({ data: { variantIds: cart.items.map((i) => i.variantId) } }),
    enabled: cart.items.length > 0,
  });
  const upsell = upsellData?.upsell ?? null;

  async function applyCode() {
    const code = codeInput.trim().toUpperCase();
    if (!code) { setDiscount(undefined); setCodeStatus(null); return; }
    const res = await validateDiscountCode({ data: { code, subtotalCents: subtotal } });
    if (res.ok) {
      setDiscount(code);
      setCodeStatus({ ok: true, msg: res.description || `Code ${code} will apply at checkout.` });
    } else {
      setDiscount(undefined);
      setCodeStatus({ ok: false, msg: res.reason });
    }
  }

  async function startCheckout() {
    if (!isPaymentsConfigured()) {
      setError("Payments aren't configured yet. Complete payments go-live in the project dashboard.");
      return;
    }
    setError(null);
    setCheckingOut(true);
    track("begin_checkout", {
      currency: "USD",
      value: subtotal / 100,
      coupon: cart.discountCode,
      items: cart.items.map((i) => ({
        item_id: i.variantId,
        item_name: `${i.productTitle} — ${i.variantName}`,
        item_variant: i.variantName,
        price: i.unitPriceCents / 100,
        quantity: i.quantity,
      })),
    });
    const { trackAbConversion, getAssignments } = await import("@/lib/ab");
    void trackAbConversion("begin_checkout");
    const abAssignments = getAssignments();
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data: userData } = await supabase.auth.getUser();
      const res = await createCheckoutSession({
        data: {
          items: cart.items.map((i) => ({ variantId: i.variantId, quantity: i.quantity })),
          discountCode: cart.discountCode,
          customerEmail: userData.user?.email,
          // user_id is derived server-side from the bearer token; never sent from the client
          returnUrl: `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`,
          environment: getStripeEnvironment(),
          utm: getStoredUtm(),
          abAssignments,
        },
      });
      if ("error" in res) { setError(res.error); setCheckingOut(false); return; }
      setClientSecret(res.clientSecret);
    } catch (e: any) {
      setError(e?.message ?? "Could not start checkout");
      setCheckingOut(false);
    }
  }

  if (clientSecret) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-bold">Checkout</h1>
        <div id="checkout" className="mt-8 overflow-hidden rounded-2xl border border-border bg-white">
          <EmbeddedCheckoutProvider stripe={getStripe()} options={{ fetchClientSecret: async () => clientSecret }}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </section>
    );
  }

  if (cart.items.length === 0) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-20 text-center sm:px-6 lg:px-8">
        <ShoppingBag className="mx-auto h-12 w-12 text-muted-foreground" />
        <h1 className="mt-4 font-display text-3xl font-semibold">Your cart is empty</h1>
        <p className="mt-2 text-muted-foreground">Pick a pack of glow balls and start your night-golf collection.</p>
        <Link to="/shop" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90">
          Shop now <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    );
  }

  const remaining = Math.max(0, FREE_SHIP_THRESHOLD - subtotal);
  const progress = Math.min(100, Math.round((subtotal / FREE_SHIP_THRESHOLD) * 100));

  return (
    <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="font-display text-3xl font-bold">Your cart</h1>

      {/* Free shipping progress */}
      <div className="mt-6 rounded-xl border border-border bg-card/50 p-4">
        <div className="flex items-center gap-2 text-sm">
          <Truck className="h-4 w-4 text-primary" />
          {remaining > 0 ? (
            <span><span className="font-semibold">{formatCents(remaining)}</span> away from free shipping</span>
          ) : (
            <span className="font-semibold text-primary">You unlocked free shipping! 🎉</span>
          )}
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
          <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <ul className="mt-6 divide-y divide-border rounded-2xl border border-border bg-card">
        {cart.items.map((i) => (
          <li key={i.variantId} className="flex items-center gap-4 p-4">
            {i.image && <img src={i.image} alt="" className="h-16 w-16 rounded-lg object-cover" />}
            <div className="min-w-0 flex-1">
              <p className="font-medium">{i.productTitle}</p>
              <p className="text-sm text-muted-foreground">{i.variantName}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => updateQuantity(i.variantId, Math.max(1, i.quantity - 1))} aria-label="Decrease quantity" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background hover:bg-secondary">−</button>
              <span className="w-6 text-center text-sm">{i.quantity}</span>
              <button onClick={() => updateQuantity(i.variantId, i.quantity + 1)} aria-label="Increase quantity" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background hover:bg-secondary">+</button>
            </div>
            <span className="w-20 text-right font-semibold">{formatCents(i.unitPriceCents * i.quantity)}</span>
            <button onClick={() => removeFromCart(i.variantId)} aria-label="Remove" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-muted-foreground hover:bg-destructive/20 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>

      {/* Order bump / upsell */}
      {upsell && (
        <div className="mt-6 rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 p-4">
          <div className="flex items-center gap-4">
            {upsell.image && <img src={upsell.image} alt="" className="h-16 w-16 rounded-lg object-cover" />}
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">{upsell.headline}</p>
              <p className="mt-1 font-medium">{upsell.productTitle} — {upsell.variantName}</p>
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{formatCents(upsell.bumpPriceCents)}</span>
                {upsell.bumpPriceCents < upsell.regularPriceCents && (
                  <span className="ml-2 line-through">{formatCents(upsell.regularPriceCents)}</span>
                )}
              </p>
            </div>
            <button
              onClick={() => addToCart({
                variantId: upsell.variantId,
                productSlug: upsell.productSlug,
                productTitle: upsell.productTitle,
                variantName: upsell.variantName,
                unitPriceCents: upsell.bumpPriceCents,
                quantity: 1,
                image: upsell.image,
              })}
              className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90"
            >
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-border bg-card/40 p-6">
        {/* Promo code */}
        <div className="mb-4">
          <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Tag className="h-3 w-3" /> Promo code
          </label>
          <div className="mt-2 flex gap-2">
            <input
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              placeholder="GLOW10"
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm uppercase placeholder:normal-case placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            />
            <button onClick={applyCode} className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-secondary">
              Apply
            </button>
          </div>
          {codeStatus && (
            <p className={`mt-2 text-xs ${codeStatus.ok ? "text-primary" : "text-destructive"}`}>{codeStatus.msg}</p>
          )}
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="font-semibold">{formatCents(subtotal)}</span>
        </div>
        <div className="mt-1 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Shipping</span>
          <span className="text-muted-foreground">{subtotal >= FREE_SHIP_THRESHOLD ? "Free" : `$5.00 (free over ${formatCents(FREE_SHIP_THRESHOLD)})`}</span>
        </div>
        {cart.discountCode && (
          <div className="mt-1 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Promo</span>
            <span className="font-medium text-primary">{cart.discountCode}</span>
          </div>
        )}
        {error && <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
        <button onClick={startCheckout} disabled={checkingOut} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90 disabled:opacity-60">
          {checkingOut ? "Starting checkout…" : "Checkout"}
        </button>
        <button onClick={clearCart} className="mt-2 w-full text-xs text-muted-foreground hover:text-foreground">
          Clear cart
        </button>
      </div>
    </section>
  );
}
